/**
 * ru-phonetics.js — школьный фонетический (звуко-буквенный) разбор ОДНОГО слова.
 *
 * Вход — слово с известным ударением (U+0301 после ударной гласной или «ё»):
 * ударение даёт база ударений сайта (/api/ru-word → accent.py readings), а не
 * этот файл. Без него разбора нет: безударные гласные звучат иначе («молоко́»
 * [малако́]). Выход — то, что пишет ученик: транскрипция, слоги, буквы → звуки
 * с характеристиками, число букв и звуков, и правило за каждым непростым звуком.
 *
 * Запись — школьная (5–11 класс): мягкость апострофом [л'], долгий согласный
 * двоеточием [с:], [й'], безударные [а]/[и]/[ы]. Разбор 1–4 класса — тот же
 * звуковой состав, другая подача (цветная схема) — её рисует страница.
 *
 * Browser: window.RuPhonetics · Node: module.exports (tests/js/ru-phonetics.test.mjs)
 */
(function (root, factory) {
    var mod = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.RuPhonetics = mod;
})(this, function () {
    'use strict';

    var ACUTE = '́';
    var VOWELS = 'аеёиоуыэюя';
    var IOTATED = { 'е': 'э', 'ё': 'о', 'ю': 'у', 'я': 'а' };
    var CONSONANTS = 'бвгджзйклмнпрстфхцчшщ';
    var ALWAYS_HARD = 'жшц';
    var ALWAYS_SOFT = 'чщй';
    var SOFTENERS = 'еёиюяь';
    var DEVOICE = { 'б': 'п', 'в': 'ф', 'г': 'к', 'д': 'т', 'ж': 'ш', 'з': 'с' };
    var VOICE = { 'п': 'б', 'ф': 'в', 'к': 'г', 'т': 'д', 'ш': 'ж', 'с': 'з' };
    var SONORANTS = 'йлмнр';
    var VOICED_UNPAIRED = 'йлмнр';
    var VOICELESS_UNPAIRED = 'хцчщ';
    // Знак мягкости — апостроф учебника (Ладыженская §6: «знак мягкости согласного ’»);
    // [ч’], [щ’], [й’] пишутся с ним, хотя мягки всегда (Разумовская: без знака
    // ученики ошибаются).
    var SOFT_MARK = '\u2019';

    // ── словарные исключения (школьный минимум) ─────────────────────────────
    // Твёрдый согласный перед «е» в заимствованиях: [кафэ́], [тэст], [мадэ́л'].
    // Источник — Еськова, «Нормы русского литературного произношения» (2015), и
    // «Произноси правильно!» Ладыженской: только слова, где твёрд КАЖДЫЙ согласный
    // перед «е». Мягкие (музей, тема, термин, кофе, пресса, кредит) сюда не входят.
    var HARD_BEFORE_E = toSet(['кафе', 'тест', 'тесты', 'теста', 'темп', 'модель', 'модели', 'компьютер', 'компьютера',
        'свитер', 'свитера', 'теннис', 'шоссе', 'энергия', 'кашне', 'пюре', 'ателье', 'отель', 'отеля', 'интернет',
        'бутерброд', 'кодекс', 'индекс', 'детектив', 'тезис', 'бизнес', 'антенна', 'майонез', 'фонетика', 'фонема',
        'продюсер', 'модерн', 'эстет', 'бифштекс', 'тоннель', 'коттедж', 'пастель', 'купе', 'тире', 'интервал',
        'интервью', 'контейнер', 'гантель', 'кабаре', 'шимпанзе', 'пенсне', 'эссе', 'турне', 'тент', 'стенд', 'тембр',
        'декольте', 'портмоне', 'протеже', 'термос', 'кафетерий', 'интернат', 'тенденция', 'протез', 'анестезия',
        'диспансер', 'партер', 'дефис', 'эстетика', 'шедевр', 'модем']);
    // Два «е» — смешанное произношение: твёрд только согласный перед ПОСЛЕДНИМ «е»
    // (Розенталь: кафетерий [ф’э…тэ], пенсне [п’э…нэ]).
    var HARD_ONLY_LAST_E = toSet(['пенсне', 'кафетерий']);
    // «чн» → [шн]: слова, где так велит норма (не «вечный», не «точный»).
    var CHN_AS_SHN = toSet(['конечно', 'скучно', 'скучный', 'скучная', 'скучное', 'скучные', 'нарочно', 'яичница',
        'скворечник', 'прачечная', 'горчичник', 'пустячный', 'ильинична', 'никитична', 'саввична', 'кузьминична',
        'лукинична', 'фоминична']);
    // «чт» → [шт] — «что» и производные от него (Розенталь: что, чтобы, что-то,
    // что-нибудь, кое-что, ничто; орфоэпический словарь: ничтожный [шт]); «нечто» — [ч’т] (МГУ)
    var CHT_AS_SHT = /^(ни|кое)?что(то|нибудь|либо|бы?)?$|^ничтож/;
    // «г» → [х] в «лёгкий», «мягкий» и их семье (гк, гч)
    var GK_AS_HK = /^(л[её]гк|л[её]гч|мягк|мягч|неле?гк|облегч)/;
    // «-ого/-его» → [в]: без морфологии — местоимения и числительные списком, а у
    // прилагательных окончание; наречия и числительные на «-ого» с корнем «-г-»
    // (много, строго, дорого) оставляют [г].
    var OGO_WORDS = toSet(['его', 'кого', 'чего', 'ничего', 'никого', 'сегодня', 'сегодняшний', 'того', 'этого',
        'моего', 'твоего', 'своего', 'нашего', 'вашего', 'всего', 'одного', 'самого', 'которого', 'какого',
        'такого', 'иного', 'некого', 'нечего', 'итого', 'сего']);
    // где «непроизносимый» согласный произносится (МГУ: бездна, компостный, безлистный, костлявый [кастл’а́вый’])
    var SOUNDED_CLUSTER = /^(бездн|компостн|безлистн|костляв)/;
    // Стык приставки и корня (предлога и слова): одинаковые или парные по
    // звонкости согласные дают ОДИН ДОЛГИЙ звук — всегда, где бы ни стояло
    // ударение (МГУ, «Долгие (двойные) согласные»: рассвет [рас:в’э́т], беззаботный,
    // бессонница [б’ис:о́н’:ица], введение [в’:ид’э́н’ий’э], отдать [ад:а́т’]).
    // Морфем движок не знает, поэтому стык ищется по приставке в начале слова
    // (с «не-»/«ни-» перед ней): за каждой приставкой — только та буква, с
    // которой её конец сливается. Корневые двойные (касса, шоссе) — правило ниже.
    var JUNCTION_PREFIX = /^(?:не|ни)?(?:в(?=в)|вз(?=з)|вс(?=с)|воз(?=з)|вос(?=с)|из(?=з)|ис(?=с)|раз(?=з)|рас(?=с)|роз(?=з)|рос(?=с)|без(?=з)|бес(?=с)|через(?=з)|черес(?=с)|пост(?=с)|с(?=[сз])|от(?=[тд])(?!томан)|под(?=[дт])|над(?=[дт])|пред(?=[дт])|об(?=[бп])|меж(?=ж)|сверх(?=х)|контр(?=р))/;
    // пары по звонкости, сливающиеся на стыке в один долгий звук второго
    var JUNCTION_PAIR = { 'тд': 1, 'дт': 1, 'сз': 1, 'зс': 1, 'бп': 1, 'пб': 1, 'вф': 1, 'фв': 1, 'гк': 1, 'кг': 1 };
    var VSTV_SILENT = /^(чувств|бесчувств|сочувств|предчувств|почувств|прочувств|здравств)/;
    var OGO_NOT = toSet(['много', 'немного', 'строго', 'нестрого', 'дорого', 'недорого', 'убого', 'полого', 'отлого',
        'поголовно']);

    function toSet(arr) { var o = {}; arr.forEach(function (w) { o[w] = 1; }); return o; }
    function isVowel(ch) { return VOWELS.indexOf(ch) >= 0; }
    function isCons(ch) { return CONSONANTS.indexOf(ch) >= 0; }

    /**
     * Буквы слова (строчные, без знаков) и номер буквы с ГЛАВНЫМ ударением: самый
     * правый знак или «ё». В сложном слове первая часть несёт побочное ударение —
     * «трёхэта́жный», «вперёдсмотря́щий», у «жёсткозакреплённый» главная — последняя
     * «ё»; то же правило у базы ударений (docs/why/primitives/stress-yo.md).
     */
    function parseStressed(word) {
        var s = String(word).toLowerCase(), letters = [], stress = -1, hyphens = [];
        for (var i = 0; i < s.length; i++) {
            var ch = s[i];
            if (ch === ACUTE) { if (letters.length && isVowel(letters[letters.length - 1])) stress = letters.length - 1; continue; }
            if (ch === '+' || ch === '̀') continue;
            // дефис — стык слов («из-за», «в-восьмых»): номер буквы после него
            if (ch === '-') { if (letters.length) hyphens.push(letters.length); continue; }
            letters.push(ch);
        }
        var yo = letters.lastIndexOf('ё');
        if (yo > stress) stress = yo;
        if (stress < 0) {
            var vs = [];
            letters.forEach(function (c, i) { if (isVowel(c)) vs.push(i); });
            if (vs.length === 1) stress = vs[0];
        }
        return { letters: letters, stress: stress, hyphens: hyphens };
    }

    function consonantSound(ch, soft) {
        return { ch: ch, kind: 'cons', soft: soft };
    }

    /** Характеристики согласного звука по школьной таблице. */
    function describeCons(s) {
        var ch = s.ch;
        var voiced, voicedPaired, softPaired;
        if (VOICED_UNPAIRED.indexOf(ch) >= 0) { voiced = true; voicedPaired = false; }
        else if (VOICELESS_UNPAIRED.indexOf(ch) >= 0) { voiced = false; voicedPaired = false; }
        else if (DEVOICE[ch]) { voiced = true; voicedPaired = true; }
        else { voiced = false; voicedPaired = true; }
        softPaired = ALWAYS_HARD.indexOf(ch) < 0 && ALWAYS_SOFT.indexOf(ch) < 0;
        return { voiced: voiced, voicedPaired: voicedPaired, softPaired: softPaired };
    }

    function symbol(s, markStress) {
        if (s.kind === 'vowel') return s.ch + (s.stressed && markStress !== false ? ACUTE : '');
        var out = s.ch;
        if (s.soft) out += SOFT_MARK;
        if (s.long) out += ':';
        return out;
    }

    /**
     * Разбор. word — слово с ударением; opts.morph — разборы pymorphy из
     * /api/ru-word (для «-ого/-его» → [в]). Слово без гласных («в», «брр») ударения
     * не требует: syllables [], noVowels; однобуквенное — clitic (предлог, частица).
     */
    function analyze(word, opts) {
        opts = opts || {};
        var p = parseStressed(word);
        var L = p.letters, stress = p.stress, n = L.length;
        var plain = L.join('');
        var notes = [];
        // Слово без гласных («в», «к», «с», «брр»): слогов и ударения нет, но
        // буквы и звуки есть — разбор строится по тем же правилам. Однобуквенное
        // слово — предлог или частица, оно звучит слитно с соседним словом,
        // поэтому конечный звонкий здесь не оглушается: «в» [в] (в саду [фсаду́] —
        // уже звук стыка двух слов, его разбор одного слова не знает).
        var noVowels = n > 0 && /^[бвгджзйклмнпрстфхцчшщьъ]+$/.test(plain);
        var clitic = noVowels && n === 1;
        if (!n || (stress < 0 && !noVowels)) return null;
        var hardE = !!HARD_BEFORE_E[plain.replace(/ё/g, 'е')];
        var hardEAt = HARD_ONLY_LAST_E[plain] ? plain.lastIndexOf('е') : -1;
        var hardBefore = function (idx) { return hardE && (hardEAt < 0 || idx === hardEAt); };

        // units: одна запись на букву; sounds — её звуки (0, 1 или 2)
        var units = L.map(function (ch, i) { return { letter: ch, i: i, sounds: [], rule: null }; });
        var lastVowel = -1;
        for (var k = 0; k < n; k++) if (isVowel(L[k])) lastVowel = k;

        for (var i = 0; i < n; i++) {
            var ch = L[i], prev = i > 0 ? L[i - 1] : '', next = i < n - 1 ? L[i + 1] : '';
            var u = units[i];
            if (ch === 'ь' || ch === 'ъ') { u.rule = ch === 'ь' ? 'soft_sign' : 'hard_sign'; continue; }
            if (isVowel(ch)) {
                var stressed = i === stress;
                var iot = (IOTATED[ch] && (i === 0 || isVowel(prev) || prev === 'ь' || prev === 'ъ'))
                    || (ch === 'и' && prev === 'ь');
                if (iot) { u.sounds.push(consonantSound('й', true)); u.rule = 'iotated'; }
                // ⚠️ indexOf('') === 0: у первой буквы «предыдущей» нет, и пустая строка
                // «нашлась» бы в любом списке — отсюда [й'ыво́] вместо [й'иво́]
                var afterHard = prev !== '' && ALWAYS_HARD.indexOf(prev) >= 0;
                var afterSoftHush = prev === 'ч' || prev === 'щ';
                var base = IOTATED[ch] || ch;
                var v = base;
                if (ch === 'и' && afterHard) { v = 'ы'; u.rule = u.rule || 'i_after_hard'; }
                if (ch === 'е' && afterHard) v = 'э';
                if (ch === 'ё' && afterHard) v = 'о';
                // «ё» не редуцируется: вне главного ударения она несёт побочное
                // (трёхэта́жный [тр’охэта́жный’]) и звучит [о] — знак ставится только главному
                if (!stressed && ch !== 'ё') {
                    if (v === 'о') v = 'а';
                    if (ch === 'е') {
                        if (i === n - 1) v = 'э';          // конец слова: мо́ре [мо́р'э], со́лнце [со́нцэ]
                        else if (afterHard) v = 'ы';       // жена́ [жына́], цена́ [цына́]
                        else v = 'и';
                    }
                    if (ch === 'я') v = (i === n - 1) ? 'а' : 'и';
                    if (ch === 'а' && afterSoftHush && i < stress) v = 'и';   // часы́ [ч'исы́], но ча́ща [ч'а́щ'а]
                    if (ch === 'е' && hardBefore(i) && isCons(prev) && !afterHard) v = 'э';
                    if (v !== base && !u.rule) u.rule = 'reduction';
                }
                u.sounds.push({ ch: v, kind: 'vowel', stressed: stressed });
                continue;
            }
            if (ch === 'й') { u.sounds.push(consonantSound('й', true)); continue; }
            if (!isCons(ch)) continue;
            var soft;
            if (ALWAYS_SOFT.indexOf(ch) >= 0) soft = true;
            else if (ALWAYS_HARD.indexOf(ch) >= 0) soft = false;
            else soft = next !== '' && SOFTENERS.indexOf(next) >= 0;
            if (soft && next === 'е' && hardBefore(i + 1)) { soft = false; u.rule = 'hard_before_e'; }
            u.sounds.push(consonantSound(ch, soft));
        }

        // ── сочетания согласных ────────────────────────────────────────────
        function consAt(j) { return units[j] && units[j].sounds.length === 1 && units[j].sounds[0].kind === 'cons' ? units[j].sounds[0] : null; }
        function drop(j, rule) { units[j].sounds = []; units[j].rule = rule; }
        var low = plain.replace(/ё/g, 'е');
        // стыки слова: дефис («из-за») и приставка, сливающаяся с корнем (JUNCTION_PREFIX)
        var junction = {};
        p.hyphens.forEach(function (j) { junction[j] = 1; });
        var jm = JUNCTION_PREFIX.exec(low);
        if (jm && jm[0].length > 0 && jm[0].length < n) junction[jm[0].length] = 1;

        // непроизносимые согласные; стц, здц (истца [исца́], под уздцы), (р)гск, (р)кск,
        // скск (петербургский, баскский) — МГУ, «Выпадение согласных»
        var SILENT = [['стн', 1], ['здн', 1], ['стл', 1], ['лнц', 0], ['рдц', 1], ['рдч', 1], ['ндц', 1],
            ['нтск', 1], ['ндск', 1], ['стск', 1], ['ндш', 1], ['нтг', 1], ['стц', 1], ['здц', 1],
            ['ргск', 1], ['ркск', 1], ['скск', 1]];
        if (!SOUNDED_CLUSTER.test(low)) {
            SILENT.forEach(function (pair) {
                var pat = pair[0], off = pair[1], at = -1;
                while ((at = plain.indexOf(pat, at + 1)) >= 0) drop(at + off, 'silent');
            });
        }
        // «вств»: немое в — в «чувство» и «здравствуй» с роднёй, а в «баловство» [ф] звучит (МГУ)
        if (VSTV_SILENT.test(low)) { var vs = plain.indexOf('вств'); if (vs >= 0) drop(vs, 'silent'); }
        // сч, зч, жч, шч, (т)сч → [щ'] (Розенталь: веснушчатый [щ’])
        for (var a = 0; a < n - 1; a++) {
            // стч, здч → [щ'], т/д не произносится: жёстче [жо́щ’э], объездчик
            if ('сз'.indexOf(L[a]) >= 0 && (L[a + 1] === 'т' || L[a + 1] === 'д') && L[a + 2] === 'ч') {
                units[a].sounds = [{ ch: 'щ', kind: 'cons', soft: true }];
                units[a].rule = 'shch';
                drop(a + 1, 'silent');
                drop(a + 2, 'merged');
                continue;
            }
            if ('сзжш'.indexOf(L[a]) >= 0 && L[a + 1] === 'ч') {
                units[a].sounds = [{ ch: 'щ', kind: 'cons', soft: true }];
                units[a].rule = 'shch';
                drop(a + 1, 'merged');
                if (a > 0 && (L[a - 1] === 'т' || L[a - 1] === 'д') && 'сз'.indexOf(L[a]) >= 0) drop(a - 1, 'silent');
            }
        }
        // тч, дч → один долгий [ч’:] (лётчик [л’о́ч’:ик], отчёт [ач’:о́т], подчеркнуть);
        // тц, дц → один [ц:] (отцы [ац:ы́]), в числительных — краткий (двадцать [два́цат’])
        // — Розенталь, МГУ; docs/ru-phonetics-conventions.md R3.21
        for (var t = 0; t < n - 1; t++) {
            if ((L[t] !== 'т' && L[t] !== 'д') || units[t].rule === 'silent' || units[t].rule === 'merged') continue;
            if (L[t + 1] === 'ч' && units[t + 1].sounds.length === 1) {
                units[t + 1].sounds = [{ ch: 'ч', kind: 'cons', soft: true, long: true }];
                units[t + 1].rule = 'tch';
                drop(t, 'merged');
            } else if (L[t + 1] === 'ц' && units[t + 1].sounds.length === 1) {
                // в числительных — краткий: двадцать, тридцать и все -надцать (МГУ: пятнадцатилетие [п’итнацат’…])
                units[t + 1].sounds = [{ ch: 'ц', kind: 'cons', soft: false, long: !/^(два|три)дцат|надцат/.test(low) }];
                units[t + 1].rule = 'tts';
                drop(t, 'merged');
            }
        }
        // стск → [ск]: немая т, и две «с» — один звук (туристский [тур’и́ск’ий’]); на стыке
        // приставки — долгий (постскриптум [пас:кр’и́птум], МГУ); скск → долгий [с:] (баскский [ба́с:к’ий’], МГУ)
        for (var sk = 0; sk < n - 3; sk++) {
            if (L[sk] !== 'с' || L[sk + 2] !== 'с' || L[sk + 3] !== 'к' || units[sk + 1].rule !== 'silent' || !consAt(sk)) continue;
            if (L[sk + 1] === 'т') {
                drop(sk + 2, 'merged');
                if (junction[sk + 2]) { units[sk].sounds[0].long = true; units[sk].rule = 'junction'; }
            } else if (L[sk + 1] === 'к') {
                drop(sk + 2, 'merged');
                units[sk].sounds[0].long = true;
            }
        }
        // з + с корня и суффикса перед согласным — один [с] (МГУ: французский [францу́ск’ий’])
        for (var zs = 0; zs < n - 2; zs++) {
            if (L[zs] === 'з' && L[zs + 1] === 'с' && isCons(L[zs + 2]) && !junction[zs + 1] && consAt(zs) && consAt(zs + 1)) drop(zs, 'merged');
        }
        // тс / дс перед к (-ский, -ство) и тся / ться → [ц]; немая т (гигантский: нтск) — не трогаем
        for (var b = 0; b < n - 1; b++) {
            var c1 = L[b], c2 = L[b + 1], c3 = L[b + 2] || '', c4 = L[b + 3] || '';
            if (units[b].rule === 'silent') continue;
            if ((c1 === 'т' || c1 === 'д') && c2 === 'с' && (c3 === 'к' || (c3 === 'т' && c4 === 'в'))) {
                units[b].sounds = [{ ch: 'ц', kind: 'cons', soft: false }]; units[b].rule = 'ts';
                drop(b + 1, 'merged');
            }
            if (c1 === 'т' && ((c2 === 'с' && c3 === 'я' && b + 3 === n) || (c2 === 'ь' && c3 === 'с' && c4 === 'я' && b + 4 === n))) {
                units[b].sounds = [{ ch: 'ц', kind: 'cons', soft: false }]; units[b].rule = 'tsa';
                if (c2 === 'ь') { drop(b + 2, 'merged'); } else { drop(b + 1, 'merged'); }
            }
        }
        // чн → [шн], чт → [шт] (словарные)
        if (CHN_AS_SHN[low]) { var cn = low.indexOf('чн'); if (cn >= 0) { units[cn].sounds = [{ ch: 'ш', kind: 'cons', soft: false }]; units[cn].rule = 'chn'; } }
        if (CHT_AS_SHT.test(low)) { var ct = low.indexOf('чт'); if (ct >= 0) { units[ct].sounds = [{ ch: 'ш', kind: 'cons', soft: false }]; units[ct].rule = 'cht'; } }
        // гк / гч → [хк] в «лёгкий», «мягкий»
        if (GK_AS_HK.test(low)) {
            var gk = low.search(/г[кч]/);
            if (gk >= 0) { units[gk].sounds[0].ch = 'х'; units[gk].rule = 'gk'; }
        }
        // «бог» → [бох] (МГУ, Розенталь)
        if (low === 'бог') { units[2].sounds[0].ch = 'х'; units[2].rule = 'bog'; }
        // -ого / -его → [в]
        if (ogoIsV(low, opts.morph)) {
            var g = low.length - 2;
            if (L[g] === 'г') { units[g].sounds[0].ch = 'в'; units[g].rule = 'ogo'; }
        }
        if (low === 'сегодня' || low === 'сегодняшний') {
            var sg = low.indexOf('г'); units[sg].sounds[0].ch = 'в'; units[sg].rule = 'ogo';
        }
        // сш, зш → [ш:], сж, зж → [ж:]; жж — долгий [ж:] в любом месте слова (МГУ:
        // жужжать [жуж:а́т’], жжёт [ж:от])
        for (var d = 0; d < n - 1; d++) {
            if (((L[d] === 'с' || L[d] === 'з') && (L[d + 1] === 'ш' || L[d + 1] === 'ж')) || (L[d] === 'ж' && L[d + 1] === 'ж')) {
                units[d + 1].sounds = [{ ch: L[d + 1], kind: 'cons', soft: false, long: true }];
                units[d + 1].rule = 'long_hush';
                drop(d, 'merged');
            }
        }
        // стык приставки (предлога) с парной по звонкости: от|дать [ад:а́т’], под|тверди́ть
        // [пат:в’ирд’и́т’], с|за́ди [з:а́д’и] — один долгий звук, звонкость и мягкость второго
        for (var jn in junction) {
            var j = +jn;
            if (j < 1 || j >= n || !JUNCTION_PAIR[L[j - 1] + L[j]] || units[j].rule || units[j - 1].rule) continue;
            var jb = consAt(j);
            if (!consAt(j - 1) || !jb) continue;
            units[j].sounds = [{ ch: jb.ch, kind: 'cons', soft: jb.soft, long: true }];
            units[j].rule = 'junction';
            drop(j - 1, 'merged');
        }
        // двойные согласные → один долгий
        for (var e = 0; e < n - 1; e++) {
            if (isCons(L[e]) && L[e] === L[e + 1] && units[e].sounds.length === 1 && units[e + 1].sounds.length === 1) {
                var s2 = units[e + 1].sounds[0];
                var atEnd = e + 1 === n - 1;
                // долгий — сразу после ударной гласной и перед гласной: ка́сса [ка́с:а],
                // ва́нна; перед ударением — обычный (шоссе́ [шасэ́]: Ладыженская «Произноси
                // правильно!», Еськова), в конце слова — тоже (класс [клас], МГУ); «одиннадцать»
                // — краткий (МГУ). На стыке приставки и корня (предлога и слова) — долгий
                // всегда: рас|ска́з [рас:ка́с], от|ту́да [ат:у́да], из-за́ [из:а́] (МГУ). Буква,
                // ставшая другим звуком (рас|сч → [щ’]), стыка не делает.
                var atJunction = !!junction[e + 1] && s2.ch === L[e];
                var afterStress = e > 0 && e - 1 === stress && e + 2 < n && isVowel(L[e + 2]) && !/^одиннадцат/.test(low);
                units[e].sounds = [{ ch: s2.ch, kind: 'cons', soft: s2.soft, long: !atEnd && (afterStress || atJunction) }];
                units[e].rule = atJunction ? 'junction' : 'double';
                drop(e + 1, 'merged');
            }
        }

        // ── смягчение перед мягким (школьная норма) ───────────────────────────
        // [н’] перед [ч’] и [щ’] — всегда (Ладыженская §60: го[н’щ’]ик, ко[н’ч’]ать);
        // зубные [с], [з], [н] перед мягкими [т’], [д’], [н’] — норма (МГУ): пра[з’н’]ик,
        // го[с’т’], ви[н’т’]ик.
        var flat = [], usedDental = false;
        units.forEach(function (un) { un.sounds.forEach(function (s) { flat.push({ s: s, u: un }); }); });
        for (var f = flat.length - 2; f >= 0; f--) {
            var a1 = flat[f].s, b1 = flat[f + 1].s;
            if (a1.kind !== 'cons' || b1.kind !== 'cons' || a1.soft) continue;
            // первая буква слова перед [т’]/[д’] — обычно приставка с- («сделать»
            // [зд’э́лат’]), там смягчения нет; перед [н’] — есть: снег [с’н’эк] (МГУ)
            var initial = flat[f].u.i === 0;
            var dental = ('сзн'.indexOf(a1.ch) >= 0 && b1.soft && 'тдн'.indexOf(b1.ch) >= 0
                && (!initial || b1.ch === 'н' || /^здеш|^здесь/.test(low)))
                // [н] перед мягкими [с’], [з’] — тоже норма (Розенталь: пе́[н’с’]ия, прете́[н’з’]ия)
                || (a1.ch === 'н' && b1.soft && 'сз'.indexOf(b1.ch) >= 0);
            var soften = (a1.ch === 'н' && (b1.ch === 'ч' || b1.ch === 'щ')) || (dental && !opts.hardDentals);
            if (dental) usedDental = true;
            if (soften) { a1.soft = true; if (!flat[f].u.rule) flat[f].u.rule = 'soft_before_soft'; }
        }

        // ── оглушение и озвончение: справа налево ────────────────────────────
        var seq = [];
        units.forEach(function (un) { un.sounds.forEach(function (s) { seq.push({ s: s, u: un }); }); });
        for (var q = seq.length - 1; q >= 0; q--) {
            var cur = seq[q].s;
            if (cur.kind !== 'cons') continue;
            var nx = q + 1 < seq.length ? seq[q + 1].s : null;
            if (!nx) {
                if (DEVOICE[cur.ch] && !clitic) { cur.ch = DEVOICE[cur.ch]; mark(seq[q].u, 'final_devoice'); }
                continue;
            }
            if (nx.kind !== 'cons' || SONORANTS.indexOf(nx.ch) >= 0) continue;
            var nxVoiced = !!DEVOICE[nx.ch] && nx.ch !== 'в';
            var nxVoiceless = !DEVOICE[nx.ch] && SONORANTS.indexOf(nx.ch) < 0;
            if (nxVoiced && VOICE[cur.ch]) { cur.ch = VOICE[cur.ch]; mark(seq[q].u, 'voicing'); }
            else if (nxVoiceless && DEVOICE[cur.ch]) { cur.ch = DEVOICE[cur.ch]; mark(seq[q].u, 'devoicing'); }
        }
        function mark(un, rule) { if (!un.rule || un.rule === 'reduction') un.rule = rule; }

        // ── итог ────────────────────────────────────────────────────────────
        var sounds = [];
        units.forEach(function (un) { un.sounds.forEach(function (s) { sounds.push(s); }); });
        // в односложном слове ударение в транскрипции не ставят: [дуп], [ноч’], [што]
        var manyVowels = sounds.filter(function (x) { return x.kind === 'vowel'; }).length > 1;
        var transcription = '[' + sounds.map(function (x) { return symbol(x, manyVowels); }).join('') + ']';
        var rows = units.map(function (un) {
            return {
                letter: un.letter,
                sounds: un.sounds.map(function (s) {
                    var o = { sym: symbol(s, manyVowels), ch: s.ch, kind: s.kind };
                    if (s.kind === 'vowel') o.stressed = !!s.stressed;
                    else {
                        var dsc = describeCons(s);
                        o.voiced = dsc.voiced; o.voicedPaired = dsc.voicedPaired;
                        o.soft = !!s.soft; o.softPaired = dsc.softPaired;
                        if (s.long) o.long = true;
                    }
                    return o;
                }),
                rule: un.rule,
            };
        });
        // Допустимые записи — там, где источники расходятся (docs/ru-phonetics-conventions.md §7):
        // твёрдые [с], [з], [н] перед мягкими зубными (многие учителя пишут так), дождь
        // [дощ’], бог [бок], -ться [ц:]. Основной ответ — норма (МГУ, Розенталь).
        var variants = [];
        if (!opts.hardDentals && !opts.noVariants && usedDental) {
            var hard = analyze(word, { morph: opts.morph, hardDentals: true, noVariants: true });
            if (hard && hard.transcription !== transcription) variants.push({ transcription: hard.transcription, note: 'hard_dentals' });
        }
        if (!opts.noVariants) {
            if (low === 'дождь') variants.push({ transcription: '[дощ' + SOFT_MARK + ']', note: 'dozhd' });
            if (low === 'бог') variants.push({ transcription: '[бок]', note: 'bog' });
            if (/т(ь)?ся$/.test(low) && transcription.indexOf('ц') >= 0) {
                variants.push({ transcription: transcription.replace(/ц(?!:)/, 'ц:'), note: 'tsa_long' });
            }
        }
        return {
            variants: variants,
            word: L.map(function (c, i) { return i === stress && c !== 'ё' ? c + ACUTE : c; }).join(''),
            letters: n,
            soundsCount: sounds.length,
            transcription: transcription,
            // гласных нет — слогов нет (в слове столько слогов, сколько гласных)
            syllables: noVowels ? [] : syllables(L, stress, units),
            rows: rows,
            vowels: sounds.filter(function (s) { return s.kind === 'vowel'; }).length,
            // слово без гласной; однобуквенное — предлог или частица, звучит слитно с соседним словом
            noVowels: noVowels,
            clitic: clitic,
        };
    }

    function ogoIsV(low, morph) {
        if (OGO_WORDS[low]) return true;
        if (!/[ое]го$/.test(low) || OGO_NOT[low] || low.length < 5) return false;
        if (Array.isArray(morph) && morph.length) {
            var m = morph[0] || {};
            return /^(ADJF|PRTF|NPRO|NUMR)$/.test(m.pos || '') && (m['case'] === 'gent' || m['case'] === 'accs');
        }
        return true;
    }

    /**
     * Слоги по школьному правилу: сколько гласных — столько слогов; согласные
     * между гласными отходят к следующему слогу, кроме сонорного (й, р, л, м, н)
     * перед шумным — он замыкает слог (лам-па, май-ка, пар-та); «й» перед
     * согласным всегда остаётся в слоге; ь и ъ — с предыдущей буквой.
     */
    function syllables(L, stress, units) {
        var n = L.length, vIdx = [];
        // непроизносимая буква на слог не влияет: со́лнце [сон-цэ] → «солн-це»
        var silent = function (j) { return !!(units && units[j] && !units[j].sounds.length && isCons(L[j])); };
        for (var i = 0; i < n; i++) if (isVowel(L[i])) vIdx.push(i);
        if (vIdx.length <= 1) return [{ text: L.join(''), stressed: true }];
        var cuts = [];
        for (var k = 0; k < vIdx.length - 1; k++) {
            var from = vIdx[k] + 1, to = vIdx[k + 1];     // letters between the vowels: [from, to)
            var mid = [];
            for (var j = from; j < to; j++) mid.push(j);
            var cut = from;
            var consOnly = mid.filter(function (j) { return L[j] !== 'ь' && L[j] !== 'ъ' && !silent(j); });
            // открытый слог: все согласные буквы (и немые) уходят в следующий слог —
            // чу-вство, пра-здник; слог закрывает только звучащий сонорный перед шумным
            // или [й’] — немая буква перед ним остаётся в прежнем слоге: солн-це, сер-дце
            if (consOnly.length >= 2) {
                var first = consOnly[0], second = consOnly[1];
                if (L[first] === 'й' || (SONORANTS.indexOf(L[first]) >= 0 && SONORANTS.indexOf(L[second]) < 0)) cut = first + 1;
            }
            // ь / ъ стоят с предыдущей буквой
            while (cut < to && (L[cut] === 'ь' || L[cut] === 'ъ')) cut++;
            cuts.push(cut);
        }
        var out = [], start = 0;
        cuts.concat([n]).forEach(function (c) {
            var text = L.slice(start, c).join('');
            out.push({ text: text, stressed: stress >= start && stress < c });
            start = c;
        });
        return out;
    }

    /**
     * Перенос (правило письма, НЕ слогоделение — docs/ru-phonetics-conventions.md §4):
     * все допустимые места переноса по Розенталю —
     *   в каждой части есть гласная; одну букву не оставляют и не переносят;
     *   согласную не отрывают от следующей гласной (соба-ка, не соб-ака);
     *   ь, ъ не отрывают от предыдущей согласной, й — от предыдущей гласной (подъ-езд, рай-он);
     *   двойные согласные разделяют (кас-са, рус-ский), но если пара кончает корень перед
     *     согласным суффикса (opts.bounds — начала морфем), её не рвут (класс-ный);
     *   приставку перед согласным не разрывают (opts.prefixEnd: под-бежать, не по-дбежать).
     * Границы морфем даёт разбор по составу (/russian-word-analysis, transferHints);
     * без них — правила букв. Возвращает ['кош-ка', …].
     */
    function transfers(word, opts) {
        var o = opts || {};
        var w = String(word || '').toLowerCase().replace(/[^а-яё]/g, '');
        var bounds = {};
        (o.bounds || []).forEach(function (b) { bounds[b] = 1; });
        var out = [];
        var nv = 0;
        for (var q = 0; q < w.length; q++) if (isVowel(w[q])) nv++;
        if (nv < 2) return out;
        var hasV = function (s) { for (var k = 0; k < s.length; k++) if (isVowel(s[k])) return true; return false; };
        for (var p = 2; p <= w.length - 2; p++) {
            var left = w.slice(0, p), right = w.slice(p);
            if (!hasV(left) || !hasV(right)) continue;
            var r0 = right[0], l1 = left[left.length - 1];
            if (r0 === 'ь' || r0 === 'ъ') continue;
            if (r0 === 'й' && isVowel(l1)) continue;
            if (isVowel(r0) && isCons(l1) && l1 !== 'й') continue;
            var pairBefore = isCons(r0) && right[1] === r0;
            var pairAfter = isCons(l1) && left[left.length - 2] === l1;
            var between = isCons(l1) && l1 === r0;
            if (between && bounds[p + 1] && !isVowel(right[1])) continue;   // класс-ный: пара — конец корня
            if (pairBefore) continue;                                        // ка-сса, ра-ссказ
            if (pairAfter && !(bounds[p] && !isVowel(r0))) continue;         // касс-а, расс-каз
            if (o.prefixEnd && p < o.prefixEnd && isCons(w[o.prefixEnd])) continue;
            out.push(left + '-' + right);
        }
        return out;
    }

    /** Коды правил у строк разбора (rows[].rule) и пометок «Допустимо также» (variants[].note) — ключи перевода страниц. */
    var RULES = ['soft_sign', 'hard_sign', 'iotated', 'i_after_hard', 'reduction', 'hard_before_e', 'silent', 'merged', 'shch',
        'tch', 'tts', 'ts', 'tsa', 'chn', 'cht', 'gk', 'bog', 'ogo', 'long_hush', 'double', 'junction', 'soft_before_soft', 'final_devoice',
        'voicing', 'devoicing'];
    var VARIANT_NOTES = ['hard_dentals', 'dozhd', 'bog', 'tsa_long'];

    return { analyze: analyze, parseStressed: parseStressed, syllables: syllables, describeCons: describeCons,
        transfers: transfers, RULES: RULES, VARIANT_NOTES: VARIANT_NOTES };
});
